import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Snowflake,
    ArrowClockwise,
    X,
    Sparkle,
    MapPin,
    Briefcase,
    ArrowRight,
} from "@phosphor-icons/react";

/**
 * RescuePanel — V2.1.1 iter170.
 *
 * Slides in as a right-side drawer from JobDetail. Lists cold candidates
 * (Rejected/Hired/archived/cold-dispositioned) in the workspace that
 * match this job by skill graph, location graph, or title bucket. Each
 * card has a one-click "Rescue into this job" button.
 */
export default function RescuePanel({ jobId, jobTitle, open, onClose, onRescued }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rescuing, setRescuing] = useState(null); // candidate_id in-flight

    const load = useCallback(async () => {
        if (!jobId) return;
        setLoading(true);
        try {
            const r = await api.get(`/jobs/${jobId}/rescue`);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load rescue candidates");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const rescue = async (cand) => {
        setRescuing(cand.candidate_id);
        try {
            const r = await api.post(`/jobs/${jobId}/rescue/${cand.candidate_id}`);
            toast.success(
                r.data.already_present
                    ? `${cand.name} was already on this pipeline`
                    : `${cand.name} rescued into Applied`
            );
            // Remove from list; parent refetches kanban.
            setData((d) => d ? {
                ...d,
                candidates: d.candidates.filter((x) => x.candidate_id !== cand.candidate_id),
                total_scored: Math.max(0, (d.total_scored || 1) - 1),
            } : d);
            onRescued?.(r.data.candidate);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rescue failed");
        } finally {
            setRescuing(null);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="rescue-panel"
            className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm flex justify-end"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md bg-white border-l-2 border-ink h-full overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="sticky top-0 bg-white border-b-2 border-ink z-10">
                    <div className="flex items-start justify-between p-5">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt inline-flex items-center gap-1">
                                <Snowflake size={10} weight="fill" /> Cold Kanban Rescue
                            </p>
                            <h2 className="font-heading text-2xl font-black tracking-tighter text-ink mt-1">
                                Rescue for {jobTitle || "this role"}
                            </h2>
                            <p className="text-xs text-[#525252] mt-1">
                                Cold candidates in your workspace who match this
                                requirement by skill, location, or title.
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            data-testid="rescue-close"
                            className="p-1 hover:bg-neutral-100"
                        >
                            <X size={18} weight="bold" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                    {loading && !data && (
                        <p className="text-sm text-[#A3A3A3]">Scoring the cold pool…</p>
                    )}
                    {data && data.candidates.length === 0 && (
                        <div className="text-center py-8">
                            <Snowflake size={32} weight="fill" className="mx-auto text-[#A3A3A3] mb-3" />
                            <p className="text-sm text-[#525252]">
                                Nobody in your cold pool matches this role right now.
                                Add more skills to the JD or widen the location to
                                surface more candidates.
                            </p>
                        </div>
                    )}
                    {data && data.candidates.length > 0 && (
                        <>
                            <p className="text-xs text-[#525252]">
                                <span className="font-bold text-ink">
                                    {data.candidates.length}
                                </span>{" "}
                                cold match{data.candidates.length === 1 ? "" : "es"}.
                                Ranked by graph overlap.
                            </p>
                            {data.candidates.map((c) => (
                                <div
                                    key={c.candidate_id}
                                    data-testid={`rescue-card-${c.candidate_id}`}
                                    className="border border-[#E5E5E5] bg-white p-3 hover:border-ink transition"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-bold text-sm text-ink truncate">
                                                {c.name || "(no name)"}
                                            </p>
                                            <p className="text-xs text-[#525252] truncate">
                                                {c.current_title || "—"}
                                            </p>
                                            <div className="flex flex-wrap items-center gap-2 mt-1 text-[10px] text-[#525252]">
                                                {c.location && (
                                                    <span className="inline-flex items-center gap-0.5">
                                                        <MapPin size={10} weight="bold" /> {c.location}
                                                    </span>
                                                )}
                                                {c.years_experience != null && (
                                                    <span className="inline-flex items-center gap-0.5">
                                                        <Briefcase size={10} weight="bold" /> {c.years_experience}y
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <span
                                            data-testid={`rescue-score-${c.candidate_id}`}
                                            className={`shrink-0 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 inline-flex items-center gap-0.5 ${
                                                c.rescue_score >= 60
                                                    ? "bg-[#ECFDF5] text-[#059669]"
                                                    : c.rescue_score >= 30
                                                    ? "bg-[#EFF6FF] text-cobalt"
                                                    : "bg-[#F5F5F5] text-[#525252]"
                                            }`}
                                        >
                                            <Sparkle size={10} weight="fill" /> {c.rescue_score}
                                        </span>
                                    </div>

                                    {/* Cold context + reasons */}
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {c.cold_reason && (
                                            <span className="text-[9px] font-black uppercase tracking-wider bg-neutral-100 text-[#525252] px-1.5 py-0.5">
                                                {c.cold_reason.replace(/_/g, " ")}
                                            </span>
                                        )}
                                        {c.prior_stage && (c.prior_stage === "Rejected" || c.prior_stage === "Hired") && (
                                            <span className="text-[9px] font-black uppercase tracking-wider bg-neutral-100 text-[#525252] px-1.5 py-0.5">
                                                Prev: {c.prior_stage}
                                            </span>
                                        )}
                                        {(c.rescue_reasons || []).map((r) => (
                                            <span
                                                key={r}
                                                title={`Matched via: ${r}`}
                                                className="text-[9px] font-black uppercase tracking-wider bg-cobalt/10 text-cobalt px-1.5 py-0.5"
                                            >
                                                {r}
                                            </span>
                                        ))}
                                    </div>

                                    <button
                                        onClick={() => rescue(c)}
                                        disabled={rescuing === c.candidate_id}
                                        data-testid={`rescue-btn-${c.candidate_id}`}
                                        className="mt-3 w-full inline-flex items-center justify-center gap-1 px-3 py-2 bg-ink text-white text-xs font-black uppercase tracking-wider hover:bg-cobalt transition disabled:opacity-60"
                                    >
                                        {rescuing === c.candidate_id ? (
                                            "Rescuing…"
                                        ) : (
                                            <>
                                                <ArrowClockwise size={12} weight="bold" />
                                                Rescue into Applied
                                                <ArrowRight size={12} weight="bold" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
